import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { X, Search, Mail, Phone, LogOut, Briefcase, Zap, Info, MessageCircle, ChevronRight, User } from 'lucide-react';
import storage from '../utils/storage';
import { hasPermission, getUserPermissions, getUserRole } from '../utils/permissions';

const NAV_ITEMS = [
  { path: '/browse-jobs',       label: 'Browse Jobs',       icon: Briefcase, employerHide: true },
  { path: '/next-gen-training', label: 'Next Gen Training', icon: Zap },
  { path: '/about',             label: 'About',             icon: Info },
  { path: '/contact-us',        label: 'Contact',           icon: MessageCircle },
];

const DEFAULT_LOGO = '/assets/imgs/theme/admin-logo-black.svg';

const MobileHeader = ({ isOpen, closeMobileMenu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userRole, setUserRole] = useState(null);
  const [userName, setUserName] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [query, setQuery] = useState('');
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);

  useEffect(() => {
    const user = storage.getItem('user');
    const token = storage.getItem('token');

    if (user && token) {
      try {
        const userData = JSON.parse(user);
        setUserRole(userData.role);
        setUserName(userData.fullName || userData.companyName || userData.email || '');
        setIsLoggedIn(true);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }

    const baseUrl = process.env.REACT_APP_BASE_URL || '';
    fetch(`${baseUrl}/api/cms/branding`)
      .then(r => r.ok ? r.json() : null)
      .then(result => {
        const hl = result?.success ? result.data?.headerLogo : '';
        if (hl) setLogoUrl(hl.startsWith('http') ? hl : `${baseUrl}${hl}`);
      })
      .catch(() => {});
  }, []);

  if (!isOpen) return null;

  const handleLogout = () => {
    storage.removeItem('token');
    storage.removeItem('user');
    setUserRole(null);
    setUserName('');
    setIsLoggedIn(false);
    closeMobileMenu();
    navigate('/');
    window.location.reload();
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = query.trim();
    closeMobileMenu();
    navigate(trimmed ? `/browse-jobs?q=${encodeURIComponent(trimmed)}` : '/browse-jobs');
  };

  const candidateMenuItems = [
    { path: '/candidate/profile', label: 'My Profile', permission: 'profile.view' },
    { path: '/candidate/applied-jobs', label: 'Applied Jobs', permission: 'applications.view' },
    { path: '/candidate/bookmarked-jobs', label: 'Saved Jobs', permission: 'candidate_bookmarks.view' },
    { path: '/candidate/interviews', label: 'Interviews', permission: 'candidate_interviews.view' },
  ];

  const visibleCandidateMenu = (() => {
    if (userRole !== 'candidate') return [];
    const currentRole = getUserRole();
    const perms = getUserPermissions();
    if (currentRole === 'superadmin' || perms.length === 0) return candidateMenuItems;
    return candidateMenuItems.filter((item) => hasPermission(item.permission));
  })();

  return (
    <>
      <div className="uc-drawer-overlay" onClick={closeMobileMenu} aria-hidden="true" />
      <aside className="uc-drawer" role="dialog" aria-modal="true" aria-label="Menu">
        <div className="uc-drawer__head">
          <Link to="/" className="uc-drawer__head-logo" onClick={closeMobileMenu} aria-label="Home">
            <img
              src={logoUrl}
              alt="Logo"
              onError={() => setLogoUrl(DEFAULT_LOGO)}
              style={{ height: 32, maxWidth: 140, objectFit: 'contain' }}
            />
          </Link>
          <button
            type="button"
            className="uc-drawer__close"
            onClick={closeMobileMenu}
            aria-label="Close menu"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSearch} className="uc-drawer__section" role="search">
          <div
            className="uc-search__field"
            style={{ border: '1px solid var(--uc-gray-300)', borderRadius: 8, padding: '0 12px' }}
          >
            <Search size={17} aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search jobs"
              aria-label="Search jobs"
            />
          </div>
        </form>

        {isLoggedIn && (userRole === 'candidate' || userRole === 'employer') ? (
          <div className="uc-drawer__section">
            <div className="uc-drawer__user-card">
              <div className="uc-drawer__avatar" aria-hidden="true">
                {userName ? userName.charAt(0).toUpperCase() : <User size={16} />}
              </div>
              <div className="uc-drawer__user-info">
                <span className="uc-drawer__user-name">{userName}</span>
                <span className="uc-drawer__user-role">{userRole === 'employer' ? 'Employer' : 'Candidate'}</span>
              </div>
            </div>
            <Link
              to={userRole === 'employer' ? '/employer/dashboard' : '/candidate/profile'}
              className="uc-drawer__link"
              onClick={closeMobileMenu}
            >
              <User size={16} aria-hidden="true" />
              {userRole === 'employer' ? 'Employer dashboard' : 'My profile'}
              <ChevronRight size={15} className="uc-drawer__link-arrow" aria-hidden="true" />
            </Link>
            {visibleCandidateMenu
              .filter((item) => item.path !== '/candidate/profile')
              .map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="uc-drawer__link"
                  onClick={closeMobileMenu}
                >
                  {item.label}
                  <ChevronRight size={15} className="uc-drawer__link-arrow" aria-hidden="true" />
                </Link>
              ))}
            <button
              type="button"
              className="uc-btn uc-btn--outline uc-btn--block"
              style={{ marginTop: 14 }}
              onClick={handleLogout}
            >
              <LogOut size={15} aria-hidden="true" /> Sign out
            </button>
          </div>
        ) : (
          <div className="uc-drawer__section">
            <div style={{ display: 'flex', gap: 10 }}>
              {userRole !== 'employer' && (
                <Link
                  to="/candidate/signin"
                  className="uc-btn uc-btn--outline"
                  style={{ flex: 1, justifyContent: 'center' }}
                  onClick={closeMobileMenu}
                >
                  Find a job
                </Link>
              )}
              {userRole !== 'candidate' && (
                <Link
                  to="/employer/signin"
                  className="uc-btn uc-btn--primary"
                  style={{ flex: 1, justifyContent: 'center' }}
                  onClick={closeMobileMenu}
                >
                  Hire now
                </Link>
              )}
            </div>
          </div>
        )}

        <nav className="uc-drawer__section uc-drawer__nav" aria-label="Mobile primary">
          <h6>Explore</h6>
          {NAV_ITEMS
            .filter(item => !(item.employerHide && userRole === 'employer'))
            .map(({ path, label, icon: Icon }) => {
              const active = location.pathname === path;
              return (
                <Link
                  key={path}
                  to={path}
                  className={`uc-drawer__navitem${active ? ' uc-drawer__navitem--active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  <span className="uc-drawer__navitem-icon" aria-hidden="true">
                    <Icon size={17} />
                  </span>
                  <span className="uc-drawer__navitem-label">{label}</span>
                  <ChevronRight size={15} className="uc-drawer__link-arrow" aria-hidden="true" />
                </Link>
              );
            })}
        </nav>

        <div className="uc-drawer__section">
          <h6>Contact</h6>
          <a href="mailto:contact@bharat-online.com" className="uc-drawer__link">
            <Mail size={16} aria-hidden="true" /> contact@bharat-online.com
          </a>
          <a href="tel:+918010844476" className="uc-drawer__link">
            <Phone size={16} aria-hidden="true" /> +91 80108 44476
          </a>
        </div>
      </aside>
    </>
  );
};

export default MobileHeader;
